function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
/** SurveyJS file questions store values as { name, type, content } where content
 * is a base64 data URI. Detect a single such file value. */
function isFileAttachmentObject(value) {
    return (isPlainObject(value) &&
        typeof value.name === 'string' &&
        typeof value.content === 'string' &&
        value.content.length > 0);
}
/** Walk the survey data and collect every file attachment with its base64 content. */
export function extractAttachments(data) {
    const attachments = [];
    function walk(value, path) {
        if (isFileAttachmentObject(value)) {
            attachments.push({
                fieldPath: path,
                name: value.name,
                type: typeof value.type === 'string' ? value.type : '',
                content: value.content,
            });
            return;
        }
        if (Array.isArray(value)) {
            value.forEach((item, index) => walk(item, `${path}[${index}]`));
            return;
        }
        if (isPlainObject(value)) {
            for (const [key, child] of Object.entries(value)) {
                walk(child, path ? `${path}.${key}` : key);
            }
        }
    }
    walk(data, '');
    return attachments;
}
/** Return a deep clone of the survey data with the heavy base64 `content` removed
 * from file attachment values, keeping the lightweight { name, type } metadata so
 * the submitted form still records which files were attached. */
export function stripAttachmentContent(data) {
    function clone(value) {
        if (isFileAttachmentObject(value)) {
            const { content: _content, ...rest } = value;
            return rest;
        }
        if (Array.isArray(value)) {
            return value.map(clone);
        }
        if (isPlainObject(value)) {
            const result = {};
            for (const [key, child] of Object.entries(value)) {
                result[key] = clone(child);
            }
            return result;
        }
        return value;
    }
    const cloned = clone(data);
    return isPlainObject(cloned) ? cloned : {};
}
/** Convert a SurveyJS data URI (or raw base64) into a Blob for direct S3 PUT. */
export function dataUriToBlob(content, mimeType = 'application/octet-stream') {
    const commaIndex = content.indexOf('base64,');
    const raw = commaIndex >= 0 ? content.slice(commaIndex + 'base64,'.length) : content;
    const mimeMatch = content.match(/^data:([^;]+);base64,/i);
    const type = mimeMatch?.[1] || mimeType;
    const binary = atob(raw);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type });
}
